import {
  Bar,
  Button,
  Dialog,
  Label,
  Option,
  RadioButton,
  ResponsiveGridLayout,
  Select,
  Title
} from '@ui5/webcomponents-react';
import { DistributionStatus, useCompleteDistributionMutation } from 'generated/graphql';
import { useEscapeFix } from 'hooks/useEscapeFix';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { validateTwoDifferentStrings } from 'utils/validator';
import { UserDialogProps } from '../../CompleteQuery';
import { useStyles } from './CompleteDistributionDialog.jss';
import { hideInternalSupplierVendor } from 'utils/hideInternalSupplierVendor';

import ButtonDesign from '@ui5/webcomponents/dist/types/ButtonDesign.js';
import TitleLevel from '@ui5/webcomponents/dist/types/TitleLevel.js';
import ValueState from '@ui5/webcomponents-base/dist/types/ValueState.js';
import WrappingType from '@ui5/webcomponents/dist/types/WrappingType.js';

export const CompleteDistributionDialog: React.FC<UserDialogProps> = (props) => {
  const classes = useStyles();
  const notSelected = 'notSelected';
  const { t } = useTranslation();
  const { open, closeConfirmationDialog, distributions, setResultMessage, user } = props;
  const [, completeDistributionMutation] = useCompleteDistributionMutation();
  const [distributionValueState, setDistributionValueState] = useState(ValueState.None);
  const [selectedActionValueState, setSelectedActionValueState] = useState(ValueState.None);
  const [selectedAction, setSelectedAction] = useState<DistributionStatus | typeof notSelected>(
    notSelected
  );
  const [selectedDistribution, setSelectedDistribution] = useState(notSelected);

  const filteredDistributionsFromSupplier = distributions || [];

  useEscapeFix(closeConfirmationDialog);

  const setInitialSelectedDistribution = () => {
    switch (true) {
      case filteredDistributionsFromSupplier.length > 1:
        setSelectedDistribution(notSelected);
        break;
      case filteredDistributionsFromSupplier.length === 1:
        setSelectedDistribution(filteredDistributionsFromSupplier[0].id);
        break;
      default:
        setSelectedDistribution('');
    }
  };

  useEffect(() => {
    if (open) {
      setInitialSelectedDistribution();
    }
  }, [open]);

  useEffect(() => {
    if (selectedDistribution !== notSelected) {
      const matchingDistribution = filteredDistributionsFromSupplier.find(
        (distribution) => distribution.id === selectedDistribution
      );

      if (matchingDistribution && matchingDistribution.status !== DistributionStatus.PENDING) {
        setSelectedAction(matchingDistribution.status);
      }
    }
  }, [selectedDistribution]);

  const selectableActions = Object.values(DistributionStatus)
    .sort()
    .filter(
      (status) =>
        status !== DistributionStatus.DRAFT &&
        status !== DistributionStatus.PENDING &&
        status !== DistributionStatus.OTHER
    );

  const completeDistribution = async () => {
    const isSelectedLanguageValid = validateTwoDifferentStrings(
      selectedDistribution,
      notSelected,
      setDistributionValueState
    );

    const isSelectedActionValid = validateTwoDifferentStrings(
      selectedAction,
      notSelected,
      setSelectedActionValueState
    );

    if (isSelectedLanguageValid && isSelectedActionValid) {
      const result = await completeDistributionMutation({
        input: {
          distributionId: Number(selectedDistribution),
          status: selectedAction as DistributionStatus
        }
      });

      if (result.error) {
        setResultMessage({
          isSuccess: false,
          message: t('complete-distribution-error-message')
        });
      } else {
        setResultMessage({
          isSuccess: true,
          message: t('complete-distribution-success-message')
        });
        resetAndClose();
      }
    }
  };

  const resetAndClose = () => {
    resetStates();
    closeConfirmationDialog();
  };

  const resetStates = () => {
    setDistributionValueState(ValueState.None);
    setSelectedActionValueState(ValueState.None);
    setSelectedAction(notSelected);
    setSelectedDistribution(notSelected);
  };

  const onChangeLanguage = (event: CustomEvent) => {
    const { value } = event.detail.selectedOption.dataset;
    validateTwoDifferentStrings(value, notSelected, setDistributionValueState);
    setSelectedDistribution(value);
  };

  return createPortal(
    <Dialog
      data-testid="complete-query-distribution-dialog"
      open={open}
      className={classes.confirmationDialog}
      header={
        <Title level={TitleLevel.H3} className={classes.confirmationHeader} wrappingType="None">
          {t('complete-query-distributed-confirmation-header')}
        </Title>
      }
      footer={
        <Bar
          className={classes.dialogFooter}
          endContent={
            <>
              <Button
                id="dialog-confirm-button"
                onClick={completeDistribution}
                design={ButtonDesign.Emphasized}
              >
                {t('confirm')}
              </Button>
              <Button id="dialog-cancel-button" onClick={resetAndClose}>
                {t('cancel')}
              </Button>
            </>
          }
        />
      }
    >
      <div className={classes.grid}>
        <Label
          id="actions-label"
          required
          showColon
          className={classes.actionLabel}
          wrappingType="None"
        >
          {t('complete-query-distributed-action-label')}
        </Label>
        <ResponsiveGridLayout
          aria-labelledby="actions-label"
          columnsXL={3}
          columnsL={2}
          columnsM={1}
          columnsS={1}
          className={classes.actions}
        >
          {[
            selectableActions.map((action) => (
              <RadioButton
                key={action}
                name="actions"
                data-testid={`DISTRIBUTION-${action}`}
                text={t(`DISTRIBUTION-${action}`)}
                checked={selectedAction === action}
                onClick={() => setSelectedAction(action)}
                valueState={selectedActionValueState}
                wrappingType="None"
              />
            )),
            <RadioButton
              key={DistributionStatus.OTHER}
              name="actions"
              text={t(`DISTRIBUTION-${DistributionStatus.OTHER}`)}
              checked={selectedAction === DistributionStatus.OTHER}
              onClick={() => setSelectedAction(DistributionStatus.OTHER)}
              valueState={selectedActionValueState}
              wrappingType="None"
            />
          ]}
        </ResponsiveGridLayout>
        <Label
          id="languages-label"
          required
          showColon
          className={classes.languageLabel}
          wrappingType="None"
        >
          {t('complete-query-distributed-language-label')}
        </Label>
        <div className={classes.languages}>
          <Select
            data-testid="languages"
            aria-labelledby="languages-label"
            onChange={onChangeLanguage}
            valueState={distributionValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('complete-query-distribution-not-selected')}
              </Label>
            }
          >
            {[
              filteredDistributionsFromSupplier.length > 1 && (
                <Option
                  key={notSelected}
                  data-value={notSelected}
                  selected={notSelected === selectedDistribution}
                >
                  {t('complete-query-distributed-language-not-selected')}
                </Option>
              ),
              filteredDistributionsFromSupplier.map((distribution) => (
                <Option
                  key={distribution.id}
                  data-value={distribution.id}
                  selected={selectedDistribution === distribution.id}
                >
                  {user?.internal
                    ? `${distribution.language.language} - ${hideInternalSupplierVendor(
                        distribution.language.supplier
                      )}`
                    : distribution.language.language}
                </Option>
              ))
            ]}
          </Select>
        </div>
      </div>
    </Dialog>,
    document.body
  );
};
